'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

const PENDING_KEY = 'yuha_scroll_to';

function scrollToSection(id: string, behavior: ScrollBehavior = 'smooth') {
  const el = document.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ behavior, block: 'start' });
  return true;
}

/**
 * Comportamentos globais da página:
 * - Links `#secao` e `/#secao` rolam até a seção sem colocar o hash na URL. Vindo de outra
 *   página (ex.: 404 → "/#planos"), guarda a seção, abre a Capa e rola depois de carregar.
 * - Elementos com `data-reveal` entram com animação quando aparecem na tela.
 */
export function PageEffects() {
  const pathname = usePathname();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      )
        return;
      const link = (e.target as Element | null)?.closest('a');
      const href = link?.getAttribute('href');
      const match = href?.match(/^(\/)?#([\w-]+)$/);
      if (!match) return;
      const [, root, id] = match;
      if ((!root || window.location.pathname === '/') && scrollToSection(id)) {
        e.preventDefault();
      } else if (root) {
        e.preventDefault();
        try {
          sessionStorage.setItem(PENDING_KEY, id);
        } catch {
          window.location.href = `/#${id}`;
          return;
        }
        window.location.href = '/';
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  useEffect(() => {
    let pending: string | null = null;
    try {
      pending = sessionStorage.getItem(PENDING_KEY);
      sessionStorage.removeItem(PENDING_KEY);
    } catch {}
    if (!pending) return;
    const id = pending;
    // Espera fontes/imagens assentarem para não parar no lugar errado
    const timer = window.setTimeout(() => scrollToSection(id), 150);
    return () => window.clearTimeout(timer);
  }, [pathname]);

  useEffect(() => {
    const items = Array.from(
      document.querySelectorAll<HTMLElement>(
        "[data-reveal='']:not(.is-visible)",
      ),
    );
    const reduce = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (reduce || !('IntersectionObserver' in window)) {
      items.forEach(el => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' },
    );
    items.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [pathname]);

  return null;
}
